import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
  DecorationLayout,
  DecorationWidth,
  type BuiltinDecorationStyle,
  type Decoration,
  type DecorationObserver,
  type EpubNavigator,
  type OnDecorationActivatedEvent,
} from '@readium/navigator';
import { Locator } from '@readium/shared';
import { extractFileNameWithoutExtension } from '../../helpers/locator';
import { getVisibleTextFragment, isQuoteVisible } from '../../helpers/readium';
import { isHighlight } from '../../helpers/selections';
import { isIframe } from '../../helpers/utils';
import type { Highlight, HighlightColor, SearchHighlight } from '../../models/selections';
import { FeatureFlagsService } from '../feature-flags/feature-flags';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';
import { TextSearchService } from '../text-search/text-search';
import { SelectionsService } from './selections';

const HIGHLIGHT_COLORS: Record<string, string> = {
  'highlight-yellow': 'rgba(255, 255, 0, .4)',
  'highlight-green': 'rgba(149, 193, 31, .4)',
  'highlight-blue': 'rgba(72, 140, 203, .4)',
  'highlight-grey': 'rgba(0, 0, 0, .12)',
  'highlight-grey-darker': 'rgba(0, 0, 0, .30)',
  // --status-shared at 30% (Figma); not in the design-system tokens yet.
  'highlight-shared': 'rgba(213, 65, 241, .3)',
};

const CURRENT_SEARCH_GROUP = 'user_current_search';
const SEARCH_RESULTS_GROUP = 'user_search_results';
const SHARED_GROUP = 'user_shared';

@Injectable({
  providedIn: 'root',
})
export class HighlightsService {
  private static readonly MAX_VISIBLE_TEXT_RETRIES = 30;
  private static readonly IMAGE_SETTLE_TIMEOUT_MS = 3000;

  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly textSearchService = inject(TextSearchService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly reconcilingHrefs = new Set<string>();
  private readonly lastAppliedKeyByGroup = new Map<string, string>();
  private readonly decorationObserver: DecorationObserver = {
    onDecorationActivated: this.handleDecorationActivated.bind(this),
  };
  private readonly sharedSelectionNavigated = signal(false);
  private loadGeneration = 0;

  readonly colorMap: Record<string, string> = {
    'highlight-yellow': 'var(--status-waiting)',
    'highlight-green': 'var(--status-success)',
    'highlight-blue': 'var(--status-informed)',
    // Only for highlights opened via a shared link; not in `colors`, so never pickable.
    'highlight-shared': 'var(--status-shared, #d541f1)',
  };
  readonly colorLabelMap: Record<string, string> = {
    'highlight-yellow': 'TEXT_SELECTION.COLOR_YELLOW',
    'highlight-green': 'TEXT_SELECTION.COLOR_GREEN',
    'highlight-blue': 'TEXT_SELECTION.COLOR_BLUE',
  };
  readonly colors: readonly HighlightColor[] = [
    'highlight-yellow',
    'highlight-green',
    'highlight-blue',
  ];

  readonly navigator = this.readerNavigatorService.navigator;
  readonly hasLoaded = computed(() => Boolean(this.readerNavigatorService.resourceLoaded.value()));
  readonly actualLocator = this.readerNavigatorService.actualLocator;
  readonly notes = computed<Highlight[]>(() =>
    this.selectionsService.selections().filter(isHighlight),
  );

  readonly highlights = computed<(Highlight | SearchHighlight)[]>(() => {
    const notes = this.notes();
    const searchHighlights = this.textSearchService.searchHighlights();
    return [...notes, ...searchHighlights];
  });

  readonly noteToEdit = signal<Highlight | undefined>(undefined);
  readonly noteIsDraft = signal<boolean>(false);
  readonly shareNote = signal<boolean>(false);
  readonly visibleHighlights = computed(() => {
    const currentLocator = this.actualLocator();
    if (!currentLocator) return [];

    this.readerNavigatorService.currentWindowIframe();
    const frames = this.readerNavigatorService.getCurrentFrameElements();

    return this.highlights()
      .filter((highlight) => {
        const highlightHref = extractFileNameWithoutExtension(highlight.locator.href);
        return highlightHref === extractFileNameWithoutExtension(currentLocator.href);
      })
      .filter((highlight) =>
        isQuoteVisible(frames, {
          before: highlight.locator.text?.before,
          highlight: highlight.locator.text?.highlight ?? '',
          after: highlight.locator.text?.after,
        }),
      );
  });

  constructor() {
    effect(() => {
      const indexSearch = this.textSearchService.indexSearch();
      if (indexSearch !== undefined) {
        // Untracked: goToLocator() reads actualLocator() synchronously, so tracking it here would
        // re-run this effect on every jump it causes and keep re-navigating in a loop.
        untracked(() => {
          void this.updateCurrentSearchHighlight(indexSearch);
        });
      }
    });

    effect(() => {
      this.textSearchService.wordToSearch(); // to trigger on search text change
      this.clearSearchHighlights();
    });

    const { navigator } = this.readerNavigatorService;
    effect(() => {
      const nav = navigator();
      if (!nav) return;
      this.colors.forEach((color) => {
        nav.registerDecorationObserver(`user_${color}`, this.decorationObserver);
      });
      // Registered (unlike SEARCH_RESULTS_GROUP) so a shared highlight can still be clicked to
      // view its note — handleDecorationActivated() just resolves it via notes(), same as any
      // other highlight. It's the `isShared` flag downstream (in the popover/note UI) that
      // hides edit/remove/share controls for it, not the absence of an observer here.
      nav.registerDecorationObserver(SHARED_GROUP, this.decorationObserver);
    });

    effect(() => {
      if (!this.hasLoaded() || this.sharedSelectionNavigated()) return;
      // Matched by id, not by `isShared`: when the owner opens their own link, their own copy
      // wins over the injected one, but the reader must still take them to it.
      const sharedId = this.selectionsService.sharedSelectionId();
      const sharedHighlight = sharedId
        ? this.notes().find((highlight) => highlight.id === sharedId)
        : undefined;
      if (!sharedHighlight) return;
      this.sharedSelectionNavigated.set(true);
      const locator = Locator.deserialize(sharedHighlight.locator);
      if (!locator) return;
      void this.readerNavigatorService.goToLocator(locator);
    });
  }

  private handleDecorationActivated(event: OnDecorationActivatedEvent): boolean {
    const highlight = this.notes().find((h) => h.id === event.decoration.id);
    if (!highlight) return false;

    const eventWindow = this.readerNavigatorService.currentWindowIframe();
    const iframeElement = eventWindow?.frameElement;
    if (!eventWindow || !(iframeElement && isIframe(iframeElement))) return false;

    // event.rect is reported in device pixels (matching the old FrameClickEvent.x/y
    // convention), unlike getBoundingClientRect() which is always in CSS pixels — on HiDPI
    // displays the un-scaled values would misposition the popover.
    const devicePixelRatio = eventWindow.devicePixelRatio || 1;
    // Matches textSelected()'s approach: only getBoundingClientRect() on the iframe itself
    // gives an origin compatible with event.rect, which is viewport-relative within the iframe.
    const iframePosition = iframeElement.getBoundingClientRect();
    const domRect = DOMRect.fromRect({
      x: event.rect.left / devicePixelRatio + iframePosition.left,
      y: event.rect.top / devicePixelRatio + iframePosition.top,
      width: event.rect.width / devicePixelRatio,
      height: event.rect.height / devicePixelRatio,
    });
    this.readerNavigatorService.selectionEdit.set(highlight);
    this.readerNavigatorService.selectionRect.set(domRect);
    // Not claimed: Readium marks a claimed activation to swallow the click that comes with it, but
    // that click has already been delivered (Peripherals posts it before Decorator does on the same
    // pointerup), so the flag would swallow the user's next click and the toolbar wouldn't close.
    return false;
  }

  private removeHighlightGroups(groups: string[]): void {
    groups.forEach((group) => {
      this.removeHighlightGroup(group);
    });
  }

  private removeHighlightGroup(group: string): void {
    const navigator = this.navigator();
    if (!navigator || !this.hasLoaded()) return;
    navigator.applyDecorations([], group);
  }

  private static groupFor(highlight: Highlight | SearchHighlight): string {
    if (highlight.color === 'highlight-grey') return SEARCH_RESULTS_GROUP;
    // Routed by flag: it has its own observer-backed group, apart from the user's `user_<color>`.
    if ('isShared' in highlight && highlight.isShared) return SHARED_GROUP;
    return `user_${highlight.color}`;
  }

  private static toDecoration(
    highlight: Highlight | SearchHighlight,
    tint: string,
  ): Decoration | undefined {
    const locator = Locator.deserialize(highlight.locator);
    if (!locator) return undefined;

    const style: BuiltinDecorationStyle = {
      tint,
      layout: DecorationLayout.Boxes,
      width: DecorationWidth.Wrap,
    };
    return { id: highlight.id, locator, style };
  }

  removeHighlight(highlight: Highlight | SearchHighlight): void {
    const navigator = this.navigator();
    if (!navigator || !this.hasLoaded()) return;

    const group = HighlightsService.groupFor(highlight);
    const remaining = this.visibleHighlights()
      .filter((h) => h.id !== highlight.id && HighlightsService.groupFor(h) === group)
      .map((h) => HighlightsService.toDecoration(h, HIGHLIGHT_COLORS[h.color]))
      .filter((decoration): decoration is Decoration => decoration !== undefined);
    navigator.applyDecorations(remaining, group);

    if (this.noteToEdit() === highlight) {
      this.noteToEdit.set(undefined);
    }
  }

  /**
   * Entry point for every "share" action: a highlight with a note opens the share dialog (so the
   * user can choose whether to include the note); one without a note copies the link directly.
   */
  async requestShare(highlight: Highlight): Promise<void> {
    if (highlight.isShared) return;
    if (highlight.notes) {
      this.noteToEdit.set(highlight);
      this.shareNote.set(true);
      return;
    }
    await this.selectionsService.shareSelectionLink(highlight);
  }

  loadVisibleHighlights(): void {
    if (!this.featureFlags.isEnabled('highlights')()) return;
    const generation = ++this.loadGeneration;
    void this.loadVisibleHighlightsWithRetry(generation);
  }

  private async loadVisibleHighlightsWithRetry(generation: number, attempt = 0): Promise<void> {
    if (generation !== this.loadGeneration) return;

    const domLooksUnsettled =
      this.visibleHighlights().length === 0 &&
      !getVisibleTextFragment(this.readerNavigatorService.getCurrentFrameElements());
    if (domLooksUnsettled && attempt < HighlightsService.MAX_VISIBLE_TEXT_RETRIES) {
      await HighlightsService.waitForNextPaint();
      await this.loadVisibleHighlightsWithRetry(generation, attempt + 1);
      return;
    }

    await this.applyVisibleHighlights();
  }

  private static async waitForNextPaint(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  private static async waitForImagesToSettle(doc: Document): Promise<boolean> {
    const pendingImages = Array.from(doc.images).filter((img) => !img.complete);
    await Promise.all(
      pendingImages.map(async (img) =>
        Promise.race([
          new Promise<void>((resolve) => {
            img.addEventListener(
              'load',
              () => {
                resolve();
              },
              { once: true },
            );
            img.addEventListener(
              'error',
              () => {
                resolve();
              },
              { once: true },
            );
          }),
          new Promise<void>((resolve) => {
            setTimeout(resolve, HighlightsService.IMAGE_SETTLE_TIMEOUT_MS);
          }),
        ]),
      ),
    );
    return pendingImages.length > 0;
  }

  private static async waitForFontsToSettle(doc: Document): Promise<boolean> {
    const fontSet = doc.fonts;
    const faces = Array.from(fontSet);
    const hadPendingFaces = faces.some((face) => face.status !== 'loaded');
    await Promise.all(faces.map(async (face) => face.load().catch(() => undefined)));
    await fontSet.ready;
    return hadPendingFaces;
  }

  private static groupKey(decorations: Decoration[]): string {
    return decorations
      .map((decoration) => decoration.id)
      .sort()
      .join(',');
  }

  private computeDecorationsByGroup(): Map<string, Decoration[]> {
    const decorationsByGroup = new Map<string, Decoration[]>();
    for (const highlight of this.visibleHighlights()) {
      const decoration = HighlightsService.toDecoration(
        highlight,
        HIGHLIGHT_COLORS[highlight.color],
      );
      if (!decoration) continue;
      const group = HighlightsService.groupFor(highlight);
      const decorations = decorationsByGroup.get(group) ?? [];
      decorations.push(decoration);
      decorationsByGroup.set(group, decorations);
    }
    return decorationsByGroup;
  }

  private static async applyDecorationsIncrementally(
    navigator: EpubNavigator,
    group: string,
    decorations: Decoration[],
  ): Promise<void> {
    const applied: Decoration[] = [];
    for (const decoration of decorations) {
      applied.push(decoration);
      navigator.applyDecorations([...applied], group);
      await HighlightsService.waitForNextPaint();
    }
  }

  private async applyVisibleHighlights(): Promise<void> {
    const navigator = this.navigator();
    if (!navigator || !this.hasLoaded()) return;

    const decorationsByGroup = this.computeDecorationsByGroup();
    for (const [group, decorations] of decorationsByGroup) {
      const key = HighlightsService.groupKey(decorations);
      if (this.lastAppliedKeyByGroup.get(group) === key) {
        navigator.applyDecorations(decorations, group);
        continue;
      }
      const isFirstApplyForGroup = !this.lastAppliedKeyByGroup.has(group);
      if (isFirstApplyForGroup && decorations.length > 1) {
        await HighlightsService.applyDecorationsIncrementally(navigator, group, decorations);
      } else {
        navigator.applyDecorations(decorations, group);
      }
      this.lastAppliedKeyByGroup.set(group, key);
    }
    if (decorationsByGroup.size === 0) return;

    // Readium computes each decoration's box geometry (getClientRects() on the located text
    // range) against the iframe's layout at the moment it receives the 'decorate' message —
    // right now, above. On a slow connection a web font or image can still be loading at that
    // point; once it finishes, the text reflows/rewraps but Readium never recomputes the
    // boxes it already sent, leaving them pointing at stale (pre-reflow) positions. Rather
    // than delaying the highlights' first appearance behind that (possibly multi-second) wait,
    // show them now and correct their position in the background once the page has genuinely
    // settled.
    void this.reconcileHighlightPositions(this.actualLocator()?.href);
  }

  private async reconcileHighlightPositions(href: string | undefined): Promise<void> {
    if (!href || this.reconcilingHrefs.has(href)) return;
    this.reconcilingHrefs.add(href);
    try {
      const windowIframe = this.readerNavigatorService.currentWindowIframe();
      const fontsWerePending = windowIframe?.document.fonts
        ? await HighlightsService.waitForFontsToSettle(windowIframe.document)
        : false;
      const imagesWerePending = windowIframe
        ? await HighlightsService.waitForImagesToSettle(windowIframe.document)
        : false;
      if (this.actualLocator()?.href !== href) return;
      if (!fontsWerePending && !imagesWerePending) return;

      const navigator = this.navigator();
      if (!navigator || !this.hasLoaded()) return;
      const decorationsByGroup = this.computeDecorationsByGroup();
      for (const [group, decorations] of decorationsByGroup) {
        navigator.applyDecorations([], group);
        await HighlightsService.applyDecorationsIncrementally(navigator, group, decorations);
      }
    } finally {
      this.reconcilingHrefs.delete(href);
    }
  }

  private clearSearchHighlights(): void {
    this.removeHighlightGroups([SEARCH_RESULTS_GROUP, CURRENT_SEARCH_GROUP]);
  }

  private async updateCurrentSearchHighlight(index: number): Promise<void> {
    const highlights = this.textSearchService.searchHighlights();
    const highlight = highlights[index];

    const locator = Locator.deserialize(highlight.locator);
    if (!locator) return;

    await this.readerNavigatorService.goToLocator(locator);
    // The search may have been closed or changed while navigating: don't repaint a stale result.
    if (this.textSearchService.searchHighlights()[index] !== highlight) return;
    const navigator = this.navigator();
    if (!navigator || !this.hasLoaded()) return;

    const decoration = HighlightsService.toDecoration(
      highlight,
      HIGHLIGHT_COLORS['highlight-grey-darker'],
    );
    navigator.applyDecorations(decoration ? [decoration] : [], CURRENT_SEARCH_GROUP);
  }
}
